import { useMemo } from "react";
import { getTierStyle } from "src/charts/types";
import type { QuadrantPractice } from "src/charts/types";
import "./PracticeListPanel.scss";

// Accessible metric colors shared with the KPI strip.
export const PA_GREEN = "#15803D";
export const ABANDON_RED = "#DC2626";

type RowItem = {
  key: string;
  name: string;
  tier: string;
  pts: number;
  pa_percentage: string;
  abandon_percentage: string;
  // Present only on practice rows (overview mode) — clicking selects it.
  raw?: QuadrantPractice;
};

type Props = {
  practices: QuadrantPractice[];
  selected: QuadrantPractice | null;
  onBack: () => void;
  onSelect?: (practice: QuadrantPractice) => void;
};

const num = (v: number | string | undefined): number => Number(v) || 0;

function TierBadge({ tier }: { tier?: string }) {
  if (!tier) return null;
  const s = getTierStyle(tier);
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 4,
        padding: "1px 8px",
        borderRadius: 20,
        fontSize: 10.5,
        fontWeight: 600,
        background: s.bg,
        color: s.text,
        border: `1px solid ${s.border}`,
        whiteSpace: "nowrap",
        flexShrink: 0,
      }}
    >
      <span
        style={{
          width: 6,
          height: 6,
          borderRadius: "50%",
          background: s.dot,
          display: "inline-block",
        }}
      />
      {tier}
    </span>
  );
}

function Row({
  item,
  maxPts,
  index,
  onClick,
}: {
  item: RowItem;
  maxPts: number;
  index: number;
  onClick?: () => void;
}) {
  const s = getTierStyle(item.tier);
  const widthPct = maxPts > 0 ? Math.max((item.pts / maxPts) * 100, 1.5) : 0;
  return (
    <div
      className={`kpiq-row${onClick ? " kpiq-row--clickable" : ""}`}
      style={{ animationDelay: `${Math.min(index, 12) * 22}ms` }}
      onClick={onClick}
      role={onClick ? "button" : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={
        onClick
          ? (e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onClick();
              }
            }
          : undefined
      }
      title={onClick ? "Click to see prescribers" : undefined}
    >
      <div className="d-flex align-items-baseline gap-2" style={{ minWidth: 0 }}>
        <span
          className="text-truncate"
          style={{ flex: 1, fontSize: 12, fontWeight: 600, color: "var(--bs-body-color)" }}
          title={item.name}
        >
          {item.name}
        </span>
        <span
          style={{
            fontSize: 12,
            fontWeight: 700,
            fontVariantNumeric: "tabular-nums",
            color: "var(--bs-body-color)",
          }}
        >
          {item.pts.toLocaleString()}
        </span>
      </div>
      <div className="d-flex align-items-center gap-2">
        <div className="kpiq-row__bar-track">
          <div
            className="kpiq-row__bar-fill"
            style={{ width: `${widthPct}%`, background: s.dot }}
          />
        </div>
        <span
          style={{
            fontSize: 10.5,
            fontVariantNumeric: "tabular-nums",
            color: PA_GREEN,
            width: 56,
            textAlign: "right",
            flexShrink: 0,
          }}
        >
          PA {item.pa_percentage}
        </span>
        <span
          style={{
            fontSize: 10.5,
            fontVariantNumeric: "tabular-nums",
            color: ABANDON_RED,
            width: 64,
            textAlign: "right",
            flexShrink: 0,
          }}
        >
          Abn {item.abandon_percentage}
        </span>
        <span
          title={item.tier}
          style={{
            width: 7,
            height: 7,
            borderRadius: "50%",
            background: s.dot,
            flexShrink: 0,
          }}
        />
      </div>
    </div>
  );
}

export default function PracticeListPanel({ practices, selected, onBack, onSelect }: Props) {
  const items = useMemo<RowItem[]>(() => {
    if (selected) {
      return (selected.drilldown?.rows ?? []).map((d, i) => ({
        key: String(d.doctor_id ?? i),
        name: d.prescriber ?? "—",
        tier: d.tier ?? "—",
        pts: num(d.pts),
        pa_percentage: d.pa_percentage ?? "—",
        abandon_percentage: d.abandon_percentage ?? "—",
      }));
    }
    return [...practices]
      .sort((a, b) => num(b.pts) - num(a.pts))
      .map((p, i) => ({
        key: String(p.practice_id ?? i),
        name: p.practice ?? "—",
        tier: p.tier ?? "—",
        pts: num(p.pts),
        pa_percentage: p.pa_percentage ?? "—",
        abandon_percentage: p.abandon_percentage ?? "—",
        raw: p,
      }));
  }, [practices, selected]);

  const maxPts = useMemo(
    () => Math.max(...items.map((i) => i.pts), 1),
    [items],
  );

  const tierStyle = getTierStyle(selected?.tier);

  return (
    <div className="kpiq-panel">
      {selected ? (
        <div
          className="border-bottom px-2"
          style={{ background: tierStyle.headerBg, paddingTop: 6, paddingBottom: 6, flexShrink: 0 }}
        >
          <div className="d-flex align-items-center gap-2" style={{ minWidth: 0 }}>
            <button
              onClick={onBack}
              className="btn btn-sm btn-outline-secondary py-0 px-2 kpiq-back"
              style={{ fontSize: 11, lineHeight: 1.7, flexShrink: 0 }}
              title="Back to all practices (Esc)"
            >
              ← All
            </button>
            <span
              className="fw-semibold text-truncate"
              style={{ flex: 1, fontSize: 12.5, color: tierStyle.text }}
              title={selected.practice}
            >
              {selected.practice ?? "—"}
            </span>
            <TierBadge tier={selected.tier} />
          </div>
          <div
            className="d-flex flex-wrap"
            style={{ fontSize: 11, color: "var(--bs-secondary-color)", gap: "0 12px", paddingLeft: 2, marginTop: 2 }}
          >
            <span>
              Cases <b style={{ color: "var(--bs-body-color)" }}>{num(selected.pts).toLocaleString()}</b>
            </span>
            <span>
              PA <b style={{ color: PA_GREEN }}>{selected.pa_percentage ?? "—"}</b>
            </span>
            <span>
              Abandon <b style={{ color: ABANDON_RED }}>{selected.abandon_percentage ?? "—"}</b>
            </span>
            <span>
              <b style={{ color: "var(--bs-body-color)" }}>{selected.drilldown?.count ?? items.length}</b> prescriber
              {(selected.drilldown?.count ?? items.length) !== 1 ? "s" : ""}
            </span>
          </div>
        </div>
      ) : (
        <div
          className="d-flex align-items-center justify-content-between border-bottom px-3"
          style={{ paddingTop: 5, paddingBottom: 5, flexShrink: 0 }}
        >
          <span
            style={{
              fontSize: 10.5,
              fontWeight: 600,
              textTransform: "uppercase",
              letterSpacing: "0.04em",
              color: "var(--bs-secondary-color)",
            }}
          >
            Practices · {items.length}
          </span>
          <span style={{ fontSize: 10.5, color: "var(--bs-secondary-color)" }}>
            by case volume
          </span>
        </div>
      )}

      {/* key remounts the list on practice change so rows re-run their entrance */}
      <div className="kpiq-panel__scroll" key={selected ? String(selected.practice_id ?? selected.practice) : "all"}>
        {items.length === 0 ? (
          <div className="text-center px-3 py-4" style={{ fontSize: 12.5, color: "var(--bs-secondary-color)" }}>
            {selected ? "No prescriber data available for this practice." : "No practices match the current filters."}
          </div>
        ) : (
          items.map((item, i) => (
            <Row
              key={item.key}
              item={item}
              maxPts={maxPts}
              index={i}
              onClick={
                onSelect && item.raw
                  ? () => onSelect(item.raw!)
                  : undefined
              }
            />
          ))
        )}
      </div>
    </div>
  );
}
